/**
 * Primary Navigation Bar
 *
 * Fixed top bar common to all pages: brand, real page links (crawlable,
 * History-API upgraded by <Link>), and auth buttons.
 */

import type { User } from '../context/AuthContext';
import Link from './Link';
import './Navbar.css';

interface NavbarProps {
  user?: User | null;
  /** Current canonical path, for the active-link state */
  path: string;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  onLogout?: () => void;
}

const NAV_ITEM_LIST: Array<{ to: string; label: string; event?: string }> = [
  { to: '/app', label: 'App' },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/docs', label: 'Docs', event: 'docs-open' },
  { to: '/learn/time-distance-diagram', label: 'Learn', event: 'learn-open' },
  { to: '/contact', label: 'Contact' },
];

function isActive(path: string, to: string): boolean {
  if (to.startsWith('/learn/')) return path.startsWith('/learn/');
  return path === to;
}

export default function Navbar({ user, path, onOpenLogin, onOpenSignup, onLogout }: NavbarProps) {
  return (
    <nav className="navbar" aria-label="Primary">
      <div className="navbar-left">
        <Link to="/" className="navbar-brand">
          <img src="/sigtdd.svg" alt="" className="navbar-logo" width="32" height="32" />
          SigTDD
        </Link>
      </div>

      <div className="navbar-center">
        {NAV_ITEM_LIST.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`navbar-link${isActive(path, item.to) ? ' navbar-link-active' : ''}`}
            aria-current={isActive(path, item.to) ? 'page' : undefined}
            event={item.event}
            source="navbar"
          >
            {item.label}
          </Link>
        ))}
      </div>

      <div className="navbar-right">
        {user ? (
          <>
            <span className="navbar-user">{user.name || user.email}</span>
            <button className="navbar-btn navbar-btn-secondary" onClick={onLogout}>
              Log Out
            </button>
          </>
        ) : (
          <>
            <button className="navbar-btn navbar-btn-secondary" onClick={onOpenLogin}>Log In</button>
            <button className="navbar-btn navbar-btn-primary" onClick={onOpenSignup}>Sign Up</button>
          </>
        )}
      </div>
    </nav>
  );
}
